import React, { useState, useMemo } from 'react'
import { useTasks, TaskGroup } from '../hooks/useTasks'
import { PartnerTask, TaskStatus, TaskPriority } from '../types/partner'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Checkbox } from '../components/ui/checkbox'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu'
import {
  Plus,
  Star,
  Search,
  CheckSquare,
  Calendar,
  MoreVertical,
  ChevronDown,
  ChevronRight,
  Trash2,
  Edit3,
  Clock,
  Activity,
  List,
  CheckCircle,
  AlertCircle,
  Menu,
  X,
  RefreshCw,
} from 'lucide-react'

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; variant: 'destructive' | 'warning' | 'info' | 'success' | 'secondary'; dot: string }> = {
  urgent: { label: 'Urgent', variant: 'destructive', dot: 'bg-rose-500' },
  high: { label: 'High', variant: 'destructive', dot: 'bg-rose-500' },
  medium: { label: 'Medium', variant: 'warning', dot: 'bg-amber-500' },
  normal: { label: 'Normal', variant: 'info', dot: 'bg-sky-500' },
  low: { label: 'Low', variant: 'success', dot: 'bg-emerald-500' },
}

const STATUS_CONFIG: Record<TaskStatus, { label: string; icon: React.ElementType }> = {
  new: { label: 'New', icon: List },
  pending: { label: 'Pending', icon: Clock },
  inprogress: { label: 'In Progress', icon: Activity },
  completed: { label: 'Completed', icon: CheckCircle },
}

const CATEGORIES = ['Reports', 'Onboarding', 'Marketplace', 'Sales', 'Support', 'Meetings', 'Project', 'Other']

// --- Sidebar ---

interface SidebarProps {
  filters: ReturnType<typeof useTasks>['filters']
  setFilters: ReturnType<typeof useTasks>['setFilters']
  counts: ReturnType<typeof useTasks>['counts']
  onAddTask: () => void
  open: boolean
  onClose: () => void
}

function TasksSidebar({ filters, setFilters, counts, onAddTask, open, onClose }: SidebarProps) {
  const statusItems: { key: TaskStatus | 'all'; label: string; icon: React.ElementType; count?: number }[] = [
    { key: 'all', label: 'All Tasks', icon: List, count: counts.total },
    { key: 'new', label: 'New', icon: List, count: counts.new },
    { key: 'pending', label: 'Pending', icon: Clock, count: counts.pending },
    { key: 'inprogress', label: 'In Progress', icon: Activity, count: counts.inprogress },
    { key: 'completed', label: 'Completed', icon: CheckCircle, count: counts.completed },
  ]

  const priorityItems: { key: TaskPriority; label: string; dot: string }[] = [
    { key: 'low', label: 'Low', dot: 'bg-emerald-500' },
    { key: 'normal', label: 'Normal', dot: 'bg-sky-500' },
    { key: 'medium', label: 'Medium', dot: 'bg-amber-500' },
    { key: 'high', label: 'High', dot: 'bg-rose-400' },
    { key: 'urgent', label: 'Urgent', dot: 'bg-rose-600' },
  ]

  const content = (
    <div className="flex flex-col h-full">
      <div className="px-3 pt-3 pb-2 flex items-center gap-2">
        <Button className="flex-1" size="sm" onClick={() => { onAddTask(); onClose(); }}>
          <Plus className="h-4 w-4 mr-1.5" />
          Add Task
        </Button>
        <button onClick={onClose} className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" aria-label="Close sidebar">
          <X className="h-4 w-4" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 pb-4 space-y-5">
        <div>
          <ul className="space-y-0.5">
            {statusItems.map(item => {
              const Icon = item.icon
              const active = (filters.status ?? 'all') === item.key
              return (
                <li key={item.key}>
                  <button
                    onClick={() => setFilters(f => ({ ...f, status: item.key as any, priority: 'all' }))}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                      active ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.count !== undefined && (
                      <span className={`text-xs tabular-nums ${active ? 'text-orange-500' : 'text-slate-400'}`}>{item.count}</span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
        <div>
          <p className="px-2.5 mb-1.5 text-[10px] font-semibold uppercase text-slate-400 tracking-wider">Priority</p>
          <ul className="space-y-0.5">
            {priorityItems.map(item => {
              const active = filters.priority === item.key
              return (
                <li key={item.key}>
                  <button
                    onClick={() => setFilters(f => ({
                      ...f,
                      priority: f.priority === item.key ? 'all' : item.key,
                      status: 'all',
                    }))}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                      active ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full flex-shrink-0 ${item.dot}`} />
                    <span>{item.label}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
        <div>
          <p className="px-2.5 mb-1.5 text-[10px] font-semibold uppercase text-slate-400 tracking-wider">View</p>
          <ul className="space-y-0.5">
            {[{ key: 'all' as const, label: 'All Tasks' }, { key: 'my' as const, label: 'My Tasks' }].map(item => {
              const active = (filters.view ?? 'all') === item.key
              return (
                <li key={item.key}>
                  <button
                    onClick={() => setFilters(f => ({ ...f, view: item.key }))}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                      active ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <CheckSquare className="h-4 w-4 flex-shrink-0" />
                    <span>{item.label}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </nav>
    </div>
  )

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:flex w-[220px] flex-shrink-0 border-r border-slate-200 bg-white">
        {content}
      </div>
      {/* Mobile sidebar overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/40" onClick={onClose} />
          <div className="relative w-[280px] bg-white shadow-xl z-50">
            {content}
          </div>
        </div>
      )}
    </>
  )
}

// --- Task Row ---

interface TaskRowProps {
  task: PartnerTask
  onToggleComplete: (id: string) => void
  onToggleStar: (id: string) => void
  onSelect: (task: PartnerTask) => void
  onEdit: (task: PartnerTask) => void
  onDelete: (id: string) => void
}

function TaskRow({ task, onToggleComplete, onToggleStar, onSelect, onEdit, onDelete }: TaskRowProps) {
  const pCfg = PRIORITY_CONFIG[task.priority]

  return (
    <li className="p-3 mb-2 border border-dashed border-slate-200 rounded-xl hover:border-slate-300 hover:bg-slate-50/50 transition-colors group">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Checkbox
            checked={task.completed}
            onCheckedChange={() => onToggleComplete(task.id)}
            className="flex-shrink-0"
          />
          <button
            onClick={() => onToggleStar(task.id)}
            className="flex-shrink-0 text-slate-300 hover:text-amber-400 transition-colors"
            aria-label={task.starred ? 'Unstar task' : 'Star task'}
          >
            <Star className={`h-4 w-4 ${task.starred ? 'fill-amber-400 text-amber-400' : ''}`} />
          </button>
          <button
            onClick={() => onSelect(task)}
            className="min-w-0 text-left"
          >
            <div className={`text-sm font-medium truncate ${task.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
              {task.title}
              <Badge variant={pCfg.variant} size="sm" className="ml-2 align-middle">{pCfg.label}</Badge>
            </div>
            <div className="text-xs text-slate-500 truncate mt-0.5">{task.description}</div>
          </button>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {task.category && (
            <Badge variant="secondary" size="sm" className="hidden md:inline-flex">{task.category}</Badge>
          )}
          {task.dueDate && (
            <span className="hidden md:inline-flex items-center text-xs text-slate-500">
              <Calendar className="h-3 w-3 mr-1" />
              {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {task.assigneeName && (
            <div className="hidden sm:flex h-7 w-7 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 items-center justify-center flex-shrink-0" title={task.assigneeName}>
              <span className="text-[10px] font-bold text-orange-700">
                {task.assigneeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </span>
            </div>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-7 w-7 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-slate-200 transition-all" aria-label="Task actions">
                <MoreVertical className="h-4 w-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(task)}>
                <Edit3 className="h-4 w-4 mr-2" /> Edit Task
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onSelect(task)}>
                <CheckSquare className="h-4 w-4 mr-2" /> View Task
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-rose-600" onClick={() => onDelete(task.id)}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete Task
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </li>
  )
}

// --- Task Group Section ---

function TaskGroupSection({
  group,
  ...rowProps
}: { group: TaskGroup } & Omit<TaskRowProps, 'task'>) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <Card className="mb-4">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center gap-2 p-4 text-left hover:bg-slate-50/50 transition-colors rounded-t-xl"
      >
        {collapsed ? <ChevronRight className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        <h5 className="font-semibold text-sm text-slate-700">{group.label}</h5>
        <span className="text-xs text-slate-400 tabular-nums">({group.tasks.length})</span>
      </button>
      {!collapsed && (
        <CardContent className="pt-0 px-4 pb-4">
          <ul className="list-none m-0 p-0">
            {group.tasks.map(task => (
              <TaskRow key={task.id} task={task} {...rowProps} />
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  )
}

// --- Add / Edit Task Dialog ---

interface TaskFormData {
  title: string
  description: string
  status: TaskStatus
  priority: TaskPriority
  category: string
  dueDate: string
  startDate: string
  assigneeName: string
}

const EMPTY_FORM: TaskFormData = {
  title: '',
  description: '',
  status: 'new',
  priority: 'normal',
  category: '',
  dueDate: '',
  startDate: '',
  assigneeName: '',
}

function taskToForm(task: PartnerTask): TaskFormData {
  return {
    title: task.title,
    description: task.description,
    status: task.status,
    priority: task.priority,
    category: task.category ?? '',
    dueDate: task.dueDate ?? '',
    startDate: task.startDate ?? '',
    assigneeName: task.assigneeName ?? '',
  }
}

interface AddEditDialogProps {
  open: boolean
  onClose: () => void
  onSave: (data: TaskFormData) => void
  initial?: TaskFormData
  mode: 'add' | 'edit'
}

function AddEditTaskDialog({ open, onClose, onSave, initial, mode }: AddEditDialogProps) {
  const [form, setForm] = useState<TaskFormData>(initial ?? EMPTY_FORM)

  React.useEffect(() => {
    if (open) setForm(initial ?? EMPTY_FORM)
  }, [open, initial])

  const update = (field: keyof TaskFormData, value: string) =>
    setForm(f => ({ ...f, [field]: value }))

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'add' ? 'Add New Task' : 'Edit Task'}</DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Create a new task for your team.' : 'Update the task details.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="task-title">Name</Label>
            <Input
              id="task-title"
              placeholder="Task name"
              value={form.title}
              onChange={e => update('title', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="task-desc">Description</Label>
            <Textarea
              id="task-desc"
              placeholder="Brief description..."
              rows={3}
              value={form.description}
              onChange={e => update('description', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="task-start">Start Date</Label>
              <Input id="task-start" type="date" value={form.startDate} onChange={e => update('startDate', e.target.value)} />
            </div>
            <div>
              <Label htmlFor="task-due">Due Date</Label>
              <Input id="task-due" type="date" value={form.dueDate} onChange={e => update('dueDate', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <select
                value={form.status}
                onChange={e => update('status', e.target.value)}
                aria-label="Task status"
                className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Priority</Label>
              <select
                value={form.priority}
                onChange={e => update('priority', e.target.value)}
                aria-label="Task priority"
                className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <select
                value={form.category}
                onChange={e => update('category', e.target.value)}
                aria-label="Task category"
                className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                <option value="">None</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="task-assignee">Assignee</Label>
              <Input
                id="task-assignee"
                placeholder="Name"
                value={form.assigneeName}
                onChange={e => update('assigneeName', e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!form.title.trim()}
            onClick={() => { onSave(form); onClose(); }}
          >
            {mode === 'add' ? 'Add Task' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Task Detail Sheet ---

interface DetailSheetProps {
  task: PartnerTask | null
  open: boolean
  onClose: () => void
  onUpdate: (id: string, updates: Partial<PartnerTask>) => void
  onDelete: (id: string) => void
  onToggleComplete: (id: string) => void
}

function TaskDetailSheet({ task, open, onClose, onUpdate, onDelete, onToggleComplete }: DetailSheetProps) {
  const [newSubtask, setNewSubtask] = useState('')

  if (!task) return null

  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return
    const updated = [...task.subtasks, { id: crypto.randomUUID(), title: newSubtask.trim(), completed: false }]
    onUpdate(task.id, { subtasks: updated })
    setNewSubtask('')
  }

  const handleToggleSubtask = (subtaskId: string) => {
    const updated = task.subtasks.map(s => s.id === subtaskId ? { ...s, completed: !s.completed } : s)
    onUpdate(task.id, { subtasks: updated })
  }

  const handleDeleteSubtask = (subtaskId: string) => {
    onUpdate(task.id, { subtasks: task.subtasks.filter(s => s.id !== subtaskId) })
  }

  const completedSubtasks = task.subtasks.filter(s => s.completed).length

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose() }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4 border-b border-slate-200">
          <SheetTitle className="text-lg">{task.title}</SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Created {new Date(task.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 pt-6">
          {/* Status & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Status</Label>
              <select
                value={task.status}
                onChange={e => onUpdate(task.id, { status: e.target.value as TaskStatus, completed: e.target.value === 'completed' })}
                aria-label="Task status"
                className="flex h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Priority</Label>
              <select
                value={task.priority}
                onChange={e => onUpdate(task.id, { priority: e.target.value as TaskPriority })}
                aria-label="Task priority"
                className="flex h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Category</Label>
              <div className="flex items-center gap-2">
                {task.category ? <Badge variant="secondary">{task.category}</Badge> : <span className="text-sm text-slate-400">None</span>}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Assignee</Label>
              <div className="flex items-center gap-2">
                {task.assigneeName ? (
                  <>
                    <div className="h-6 w-6 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                      <span className="text-[9px] font-bold text-orange-700">
                        {task.assigneeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm text-slate-700">{task.assigneeName}</span>
                  </>
                ) : (
                  <span className="text-sm text-slate-400">Unassigned</span>
                )}
              </div>
            </div>
          </div>

          {/* Dates */}
          {(task.startDate || task.dueDate) && (
            <div className="grid grid-cols-2 gap-4">
              {task.startDate && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1.5 block">Start Date</Label>
                  <span className="text-sm text-slate-700">{new Date(task.startDate).toLocaleDateString()}</span>
                </div>
              )}
              {task.dueDate && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1.5 block">Due Date</Label>
                  <span className="text-sm text-slate-700">{new Date(task.dueDate).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Description</Label>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {task.description || <span className="text-slate-400 italic">No description</span>}
            </p>
          </div>

          {/* Subtasks */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs text-slate-500">
                Subtasks {task.subtasks.length > 0 && `(${completedSubtasks}/${task.subtasks.length})`}
              </Label>
            </div>
            {task.subtasks.length > 0 && (
              <div className="mb-3 rounded-lg border border-slate-200 divide-y divide-slate-100">
                {task.subtasks.map(sub => (
                  <div key={sub.id} className="flex items-center gap-2.5 px-3 py-2 group/sub">
                    <Checkbox
                      checked={sub.completed}
                      onCheckedChange={() => handleToggleSubtask(sub.id)}
                    />
                    <span className={`flex-1 text-sm ${sub.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                      {sub.title}
                    </span>
                    <button
                      onClick={() => handleDeleteSubtask(sub.id)}
                      className="opacity-0 group-hover/sub:opacity-100 p-0.5 rounded hover:bg-slate-100 transition-all"
                      aria-label="Remove subtask"
                    >
                      <X className="h-3 w-3 text-slate-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Add a subtask..."
                value={newSubtask}
                onChange={e => setNewSubtask(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddSubtask() }}
                className="h-8 text-sm"
              />
              <Button size="sm" variant="outline" onClick={handleAddSubtask} disabled={!newSubtask.trim()}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
            <Button
              variant={task.completed ? 'outline' : 'success'}
              size="sm"
              onClick={() => onToggleComplete(task.id)}
            >
              <CheckCircle className="h-4 w-4 mr-1.5" />
              {task.completed ? 'Mark Incomplete' : 'Mark Complete'}
            </Button>
            <div className="flex-1" />
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { onDelete(task.id); onClose(); }}
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

// --- Main TasksView ---

export default function TasksView() {
  const {
    groupedTasks,
    isLoading,
    error,
    filters,
    setFilters,
    counts,
    addTask,
    updateTask,
    deleteTask,
    toggleComplete,
    toggleStar,
    filteredTasks,
    refresh,
  } = useTasks()

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<PartnerTask | null>(null)
  const [selectedTask, setSelectedTask] = useState<PartnerTask | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const handleAddSave = (data: TaskFormData) => {
    addTask({
      title: data.title,
      description: data.description,
      status: data.status,
      priority: data.priority,
      category: data.category || undefined,
      assigneeName: data.assigneeName || undefined,
      dueDate: data.dueDate || undefined,
      startDate: data.startDate || undefined,
      starred: false,
      completed: data.status === 'completed',
      tags: [],
      subtasks: [],
    })
  }

  const handleEditSave = (data: TaskFormData) => {
    if (!editingTask) return
    updateTask(editingTask.id, {
      title: data.title,
      description: data.description,
      status: data.status,
      priority: data.priority,
      category: data.category || undefined,
      assigneeName: data.assigneeName || undefined,
      dueDate: data.dueDate || undefined,
      startDate: data.startDate || undefined,
      completed: data.status === 'completed',
    })
    if (selectedTask?.id === editingTask.id) {
      setSelectedTask(prev => prev ? { ...prev, ...data, completed: data.status === 'completed' } : null)
    }
  }

  const handleSelectTask = (task: PartnerTask) => {
    setSelectedTask(task)
    setDetailOpen(true)
  }

  const handleDetailUpdate = (id: string, updates: Partial<PartnerTask>) => {
    updateTask(id, updates)
    setSelectedTask(prev => prev && prev.id === id ? { ...prev, ...updates, updatedAt: new Date().toISOString() } : prev)
  }

  // Keep selectedTask in sync when tasks change
  const syncedSelectedTask = useMemo(() => {
    if (!selectedTask) return null
    return filteredTasks.find(t => t.id === selectedTask.id) ?? selectedTask
  }, [selectedTask, filteredTasks])

  if (isLoading) {
    return (
      <div className="flex-1 bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-3 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500 font-medium">Loading tasks...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex overflow-hidden bg-slate-50">
      <TasksSidebar
        filters={filters}
        setFilters={setFilters}
        counts={counts}
        onAddTask={() => setAddDialogOpen(true)}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Content header */}
        <div className="flex items-center gap-3 px-6 py-3 bg-white border-b border-slate-200 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Open sidebar"
          >
            <Menu className="h-5 w-5 text-slate-600" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <CheckSquare className="h-4 w-4 mr-2" />
                {filters.view === 'my' ? 'My Tasks' : 'All Tasks'}
                <ChevronDown className="h-3.5 w-3.5 ml-1.5 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setFilters(f => ({ ...f, view: 'all' }))}>All Tasks</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilters(f => ({ ...f, view: 'my' }))}>My Tasks</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search tasks..."
              value={filters.search ?? ''}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="h-8 pl-8 text-sm"
            />
          </div>
          <div className="flex-1" />
          <Button variant="ghost" size="icon-sm" onClick={refresh} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Task
          </Button>
        </div>

        {/* Task list */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {filteredTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <CheckSquare className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-base font-semibold text-slate-700 mb-1">No tasks found</h3>
              <p className="text-sm text-slate-500 mb-4">
                {filters.search ? 'Try adjusting your search or filters.' : 'Get started by creating your first task.'}
              </p>
              {!filters.search && (
                <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Task
                </Button>
              )}
            </div>
          ) : (
            <div className="max-w-[1200px]">
              {groupedTasks.map(group => (
                <TaskGroupSection
                  key={group.label}
                  group={group}
                  onToggleComplete={toggleComplete}
                  onToggleStar={toggleStar}
                  onSelect={handleSelectTask}
                  onEdit={t => { setEditingTask(t); }}
                  onDelete={deleteTask}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Task Dialog */}
      <AddEditTaskDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleAddSave}
        mode="add"
      />

      {/* Edit Task Dialog */}
      <AddEditTaskDialog
        open={!!editingTask}
        onClose={() => setEditingTask(null)}
        onSave={handleEditSave}
        initial={editingTask ? taskToForm(editingTask) : undefined}
        mode="edit"
      />

      {/* Task Detail Sheet */}
      <TaskDetailSheet
        task={syncedSelectedTask}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdate={handleDetailUpdate}
        onDelete={deleteTask}
        onToggleComplete={toggleComplete}
      />
    </div>
  )
}
